/*
 * NOVDAT T13 — Site-wide persistent sticky add-to-cart bar.
 *
 * On any product page, snapshots the currently-viewed variant into
 * sessionStorage (mirrors the PDP's own native <sticky-add-to-cart> DOM,
 * so it stays correct across variant changes for free -- see
 * #captureFromPdp below). On any other page (except cart), if a snapshot
 * exists, renders a bar using the same .sticky-add-to-cart__bar markup/
 * styles as the PDP bar (snippets/sticky-add-to-cart-styles.liquid),
 * shown on load and hidden while the page's own footer is in view.
 *
 * Deliberately a separate, self-contained system from
 * assets/sticky-add-to-cart.js -- same visual language and general
 * "watch an IntersectionObserver, toggle a stuck bar" approach, but no
 * shared code, per T13's scope.
 */
import { getIntersectionRoot } from '@theme/scroll-container';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';

const STORAGE_KEY = 'novdat-persistent-sticky-bar';

/**
 * @typedef {Object} StoredProduct
 * @property {string} handle
 * @property {string} url
 * @property {string} title
 * @property {string} variantTitle
 * @property {string} price
 * @property {string} image
 * @property {string} variantId
 * @property {number} savedAt
 */

/** @returns {StoredProduct | null} */
function getStoredProduct() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.variantId) return null;
    return parsed;
  } catch (_) {
    return null;
  }
}

/** @param {StoredProduct} product */
function setStoredProduct(product) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(product));
  } catch (_) {
    // sessionStorage unavailable (private mode, quota, etc.) -- feature
    // just doesn't persist, nothing else depends on it.
  }
}

function clearStoredProduct() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    // no-op
  }
}

/**
 * Reduces a variant id (raw numeric id or a `gid://shopify/ProductVariant/N`
 * string) down to its trailing numeric id, so ids from different sources
 * (Liquid-rendered raw ids vs. GID-wrapped event payloads) can be compared.
 * @param {string | number | null | undefined} id
 * @returns {string}
 */
function normalizeVariantId(id) {
  if (id == null) return '';
  const match = String(id).match(/(\d+)$/);
  return match ? match[1] : String(id);
}

function getPageTemplate() {
  // NOVDAT T14: `Theme` is declared with `const` in a classic (non-module)
  // <script> in snippets/scripts.liquid, so it never becomes a `window`
  // property (only `var`/function declarations do that) -- but it IS
  // resolvable as a bare global identifier from module scripts, since both
  // share the same realm's global scope chain. `window.Theme` is always
  // undefined here; every other native file in this theme (product-form.js,
  // cart-discount.js, predictive-search.js, etc.) reads the bare `Theme`
  // global for exactly this reason.
  return Theme?.template?.name ?? '';
}

/**
 * Captures the current PDP's selected variant from the native
 * <sticky-add-to-cart> bar's own DOM (already kept correct across variant
 * changes by assets/sticky-add-to-cart.js's morph), so this file doesn't
 * need to re-implement variant tracking or price/image formatting.
 */
function captureFromPdp() {
  const stickyBar = document.querySelector('sticky-add-to-cart');
  const variantPicker = document.querySelector('variant-picker[data-template-product-match="true"]');
  if (!stickyBar || !variantPicker) return;

  const variantId = stickyBar.dataset.currentVariantId;
  if (!variantId || stickyBar.dataset.variantAvailable !== 'true') return;

  const titleEl = stickyBar.querySelector('.sticky-add-to-cart__title');
  const variantEl = stickyBar.querySelector('.sticky-add-to-cart__variant');
  const priceEl = stickyBar.querySelector('.sticky-add-to-cart__price');
  const imageEl = stickyBar.querySelector('.sticky-add-to-cart__image-img');

  setStoredProduct({
    handle: variantPicker.dataset.productHandle ?? '',
    url: variantPicker.dataset.productUrl ?? '',
    title: titleEl?.textContent?.trim() ?? '',
    variantTitle: variantEl?.textContent?.trim() ?? '',
    price: priceEl?.textContent?.trim() ?? '',
    image: imageEl?.getAttribute('src') ?? '',
    variantId,
    savedAt: Date.now(),
  });
}

/**
 * A site-wide sticky bar that follows the shopper's most recently viewed
 * product across pages (excluding the product's own PDP and the cart
 * page), backed by sessionStorage.
 */
class PersistentStickyBar extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #footerObserver = null;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    const template = getPageTemplate();

    if (template === 'product') {
      this.#watchPdp();
      return;
    }

    if (template === 'cart') return;

    this.#watchGlobalCartUpdates();

    const product = getStoredProduct();
    if (product) this.#render(product);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#footerObserver?.disconnect();
  }

  /**
   * On the PDP itself: just keep sessionStorage in sync, no bar UI here --
   * the page's own native <sticky-add-to-cart> handles that.
   */
  #watchPdp() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    captureFromPdp();

    document.addEventListener(
      StandardEvents.productSelect,
      (event) => {
        if (!(event.target instanceof Element)) return;
        event.promise
          ?.then(() => {
            requestAnimationFrame(captureFromPdp);
          })
          .catch(() => {});
      },
      { signal }
    );

    this.#watchGlobalCartUpdates();
  }

  /**
   * Clears the stored product (and, if currently shown, the bar) the
   * moment its variant is added to cart from ANYWHERE -- the real PDP buy
   * box, the PDP's own sticky bar, or this bar's own button. All of those
   * paths dispatch the same shopify:cart:lines-update event.
   */
  #watchGlobalCartUpdates() {
    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      (event) => {
        if (event.action !== 'add' || !Array.isArray(event.lines)) return;

        const stored = getStoredProduct();
        if (!stored) return;

        const storedId = normalizeVariantId(stored.variantId);
        const wasAdded = event.lines.some((line) => normalizeVariantId(line.merchandiseId) === storedId);
        if (!wasAdded) return;

        clearStoredProduct();
        this.#hide(true);
      },
      this.#abortController ? { signal: this.#abortController.signal } : undefined
    );
  }

  /** @param {StoredProduct} product */
  #render(product) {
    const hasImage = Boolean(product.image);

    this.innerHTML = `
      <div class="sticky-add-to-cart__bar persistent-sticky-bar__bar" data-stuck="false" role="region" aria-label="${escapeHtml(
        product.title
      )}">
        ${
          hasImage
            ? `<div class="sticky-add-to-cart__image">
                <img class="sticky-add-to-cart__image-img" src="${escapeHtml(product.image)}" alt="${escapeHtml(
                product.title
              )}" loading="lazy">
              </div>`
            : ''
        }
        <div class="sticky-add-to-cart__info" data-has-image="${hasImage}">
          <p class="sticky-add-to-cart__title">${escapeHtml(product.title)}</p>
          ${
            product.variantTitle
              ? `<div class="sticky-add-to-cart__variant">${escapeHtml(product.variantTitle)}</div>`
              : ''
          }
        </div>
        <div class="sticky-add-to-cart__price">${escapeHtml(product.price)}</div>
        <button type="button" class="sticky-add-to-cart__button add-to-cart-button button" ref="addButton">
          <span class="add-to-cart-text">
            <span class="add-to-cart-text__content"><span>Add to cart</span></span>
          </span>
        </button>
      </div>
    `;

    const bar = /** @type {HTMLElement} */ (this.querySelector('.persistent-sticky-bar__bar'));
    const button = /** @type {HTMLButtonElement} */ (this.querySelector('[ref="addButton"]'));

    button.addEventListener('click', () => this.#addToCart(product, button));

    this.#watchFooter(bar);
  }

  /**
   * Shows the bar on load, hides it while the page's own footer is in
   * view, shows it again once scrolled back up -- same general technique
   * as sticky-add-to-cart.js's own footer observer, kept separate per
   * this ticket's scope (no shared code).
   * @param {HTMLElement} bar
   */
  #watchFooter(bar) {
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');

    if (!footer) {
      bar.dataset.stuck = 'true';
      return;
    }

    this.#footerObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;
        bar.dataset.stuck = entry.isIntersecting ? 'false' : 'true';
      },
      { root: getIntersectionRoot() }
    );

    this.#footerObserver.observe(footer);
  }

  /**
   * @param {StoredProduct} product
   * @param {HTMLButtonElement} button
   */
  async #addToCart(product, button) {
    if (button.disabled) return;
    button.disabled = true;

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'standard-action',
        lines: [{ merchandiseId: product.variantId, quantity: 1 }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: product.variantId, quantity: 1 }] }),
      });

      if (!addResponse.ok) {
        const errorBody = await addResponse.json().catch(() => ({}));
        throw new Error(errorBody?.description || 'Add to cart failed');
      }

      const cart = await fetch('/cart.js').then((res) => res.json());

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, source: 'persistent-sticky-bar' },
      });

      clearStoredProduct();
      this.#hide(true);
    } catch (error) {
      deferredEventPromise.reject(error);
      button.disabled = false;
      console.warn('[persistent-sticky-bar] Add to cart failed:', error);
    }
  }

  /** @param {boolean} remove */
  #hide(remove) {
    const bar = this.querySelector('.persistent-sticky-bar__bar');
    if (bar instanceof HTMLElement) bar.dataset.stuck = 'false';
    if (!remove) return;

    this.#footerObserver?.disconnect();
    this.#footerObserver = null;
    setTimeout(() => {
      this.innerHTML = '';
    }, 350);
  }
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

if (!customElements.get('persistent-sticky-bar')) {
  customElements.define('persistent-sticky-bar', PersistentStickyBar);
}
